/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useRef } from "react";
import { IFieldValidationError } from "./FieldTypes";
import { FieldWrapper } from "./FieldWrapper";
import { BEM } from "@adapptio/lib-components";
import { ISchemaStringWithConstConstItem } from "@adapptio/lib-blueprint";
import { useToggle } from "@adapptio/lib-components/src/Hooks/useToggle";
import { ColorPicker, ColorPickerTabs, ColorPickerTile, IColorPickerProps } from "../ColorPicker";
import {
	FIELD_FLOATING_PANEL_SCHEMA,
	FIELD_FLOATING_PANEL_STYLE,
	FieldFloatingPanel
} from "./FieldFloatingPanel";
import { FC_ANCHOR, FC_DIRECTION, FC_SIZE } from "../FloatingContainer";
import { Icon, ICON_SIZE } from "../Icon";
import { Input } from "../Input";
import { InputAdornment } from "../InputAdornment";
import { InputButton } from "../InputButton";
import { FieldValidationErrors } from "./FieldValidationErrors";

export interface IFieldColorProps {
	id?: string;
	label?: string;
	value: string;
	constants?: ISchemaStringWithConstConstItem[];
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	onChange?: (value: string) => void;
	grow?: boolean;
}

export const FieldColor: FC<IFieldColorProps> = ({
	id,
	label,
	value,
	constants,
	placeholder,
	readOnly,
	validationErrors,
	className,
	grow,
	onChange
}) => {
	const fieldRef = useRef();
	const [ pickerOpen, togglePicker, openPicker, closePicker ] = useToggle();
	const baseClass = BEM("edt-field-control", null, {
		"picker-open": pickerOpen
	});

	const handleChange = useCallback<IColorPickerProps["onChange"]>(
		(value) => {
			if (onChange) {
				onChange(value);
			}
		},
		[ onChange ]
	);

	const handleChangeInput = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
		(event) => {
			const val = event.target.value;

			onChange(val);
		},
		[ onChange ]
	);

	const handleOpenPickerClick = useCallback<React.MouseEventHandler<HTMLElement>>(
		(event) => {
			if (readOnly) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			openPicker();
		},
		[ openPicker, readOnly ]
	);

	const handleTogglePickerClick = useCallback<React.MouseEventHandler<HTMLElement>>(
		(event) => {
			if (readOnly) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			togglePicker();
		},
		[ togglePicker, readOnly ]
	);

	return (
		<FieldWrapper
			type="color"
			className={className}
			grow={grow}
			wrapperRef={fieldRef}
			onClick={handleOpenPickerClick}
			isFocused
		>
			<ColorPicker
				palette={constants}
				value={value || ""}
				onChange={handleChange}
				onClose={closePicker}
			>
				<Input
					id={id}
					className={baseClass.toClassName()}
					type="text"
					value={value ?? ""}
					placeholder={placeholder}
					readOnly={readOnly}
					onChange={handleChangeInput}
					startAdornment={
						<InputAdornment square center>
							<ColorPickerTile className={baseClass.element("preview").toClassName()} />
						</InputAdornment>
					}
					endAdornment={
						<InputAdornment>
							<FieldValidationErrors errors={validationErrors} />
							<InputButton
								className={baseClass.element("picker-trigger").toClassName()}
								onClick={handleTogglePickerClick}
								tabIndex={-1}
								disabled={readOnly}
							>
								<Icon
									className={baseClass.element("picker-trigger-icon").toClassName()}
									name="mdi/menu-down"
									size={ICON_SIZE.XSMALL}
								/>
							</InputButton>
						</InputAdornment>
					}
				/>
				<FieldFloatingPanel
					title={label}
					for={fieldRef}
					isOpened={pickerOpen}
					anchor={FC_ANCHOR.CENTER_BOTTOM}
					direction={FC_DIRECTION.HR}
					size={FC_SIZE.FIT_MIN}
					style={FIELD_FLOATING_PANEL_STYLE.STICKY}
					schema={FIELD_FLOATING_PANEL_SCHEMA.PRIMARY}
					onClose={closePicker}
				>
					<ColorPickerTabs />
				</FieldFloatingPanel>
			</ColorPicker>
		</FieldWrapper>
	);
};
